import React, { useEffect, useState, useCallback } from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const REASON_LABELS = {
  unknown_license: 'Clé de licence inconnue',
  inactive_license: 'Licence désactivée',
  invalid_payload: 'Données de partie refusées (format invalide)',
};

const fmtWhen = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

/**
 * 17/07/2026 — Diagnostic « apprenants invisibles » : liste les snapshots
 * télémétrie REFUSÉS par le backend (clé inconnue / licence désactivée).
 * Ces rejets étaient silencieux → le formateur ne voyait juste « aucune
 * donnée ». Rendu uniquement s'il y a au moins un rejet.
 */
export default function TelemetryRejectsBlock({ token }) {
  const [rejects, setRejects] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchRejects = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${API}/api/admin/telemetry-rejects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setRejects(Array.isArray(data?.rejects) ? data.rejects : []);
      }
    } catch (_) { /* réseau — silencieux */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { if (token) fetchRejects(); }, [token, fetchRejects]);

  if (rejects.length === 0) return null;
  return (
    <div
      className="bg-amber-500/5 border border-amber-500/40 rounded-xl p-4"
      data-testid="telemetry-rejects-block"
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-amber-300 flex items-center gap-2">
          <WifiOff className="w-4 h-4" />
          Données non remontées — {rejects.length} clé{rejects.length > 1 ? 's' : ''} rejetée{rejects.length > 1 ? 's' : ''}
        </h3>
        <button
          onClick={fetchRejects}
          disabled={loading}
          className="text-[10px] text-slate-400 hover:text-slate-200 flex items-center gap-1 transition-colors"
          data-testid="telemetry-rejects-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Actualiser
        </button>
      </div>
      <p className="text-[11px] text-slate-400 mb-3">
        Ces apprenants jouent mais leurs snapshots sont refusés par le serveur.
        {rejects.some((r) => r.reason === 'invalid_payload') && (
          <span className="block text-amber-200/80 mt-0.5">
            « Format invalide » : bug côté jeu — <strong>aucune action apprenant requise</strong>,
            c&apos;est corrigé par une mise à jour de l&apos;application (redéploiement).
          </span>
        )}
        {rejects.some((r) => r.reason === 'unknown_license' || r.reason === 'inactive_license') && (
          <span className="block mt-0.5">
            « Clé inconnue / désactivée » : vérifiez la clé dans l&apos;administration puis faites-la ressaisir à l&apos;apprenant.
          </span>
        )}
        <span className="block text-slate-500 mt-0.5 italic">
          Une entrée disparaît automatiquement dès qu&apos;un snapshot de cette clé est de nouveau accepté.
        </span>
      </p>
      <div className="space-y-1.5">
        {rejects.map((r) => (
          <div
            key={r.license_key}
            className="flex items-center gap-3 text-[11px] font-mono bg-ink-800/60 rounded-md px-2.5 py-1.5 flex-wrap"
            data-testid={`telemetry-reject-${r.license_key}`}
          >
            <span className="text-slate-200 font-bold">{r.license_key}</span>
            <span className="text-rose-300">{REASON_LABELS[r.reason] || r.reason}</span>
            {r.detail && <span className="text-amber-300/80" title={r.detail}>{r.detail}</span>}
            {r.company_name && <span className="text-slate-400">« {r.company_name} »</span>}
            <span className="text-slate-500">{r.attempts || 1} tentative{(r.attempts || 1) > 1 ? 's' : ''}</span>
            <span className="text-slate-500 ml-auto">{fmtWhen(r.last_seen)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
